/**
 * ADR-118 — cookie clear-all-scopes helper.
 *
 * Reusable client-side cookie clear. Browsers treat cookies with
 * distinct `Domain` attributes as distinct entries; a single
 * `document.cookie = 'foo=;'` unsets only the no-Domain variant.
 * Iterating across candidate scopes covers every env the cookie may
 * have been set under.
 *
 * Best-effort by design — the server-side authoritative clear
 * (`POST /api/v2/auth/logout/`) is what truly invalidates the cookie.
 * This helper exists so the React client side also clears immediately
 * rather than waiting for a server round-trip on logout.
 */

/**
 * Default Domain scopes to iterate. Order is intentional:
 *   1. Empty string → no Domain attribute (covers localdev)
 *   2. `location.hostname` → exact-match domain (defensive; some
 *      cookies are set with the exact host)
 *   3. `.tiaki.localhost` → local-dev parent domain (legacy)
 *   4. `.aegix.global` → prod parent domain
 *
 * Apps can extend or replace this list via `options.candidateDomains`.
 */
export const DEFAULT_CANDIDATE_DOMAINS: readonly string[] = [
  '',
  '.tiaki.localhost',
  '.aegix.global',
];

/**
 * Derive the env-scoped parent `Domain` candidates from a
 * hostname by walking up its labels, e.g.
 * `aim.dev.aegix.global` → [`.dev.aegix.global`, `.aegix.global`].
 *
 * The server sets the shared session cookie at the env-scoped parent
 * (`jwt_cookie_domain`: drop the leading app label), which on dev/staging is
 * `.dev.aegix.global` / `.staging.aegix.global` — scopes that
 * `DEFAULT_CANDIDATE_DOMAINS` (prod's `.aegix.global` only) never clears, so a
 * lingering parent-domain cookie survives logout there. Deriving from the live
 * hostname covers every env without a hardcoded per-env list.
 *
 * Stops one label short of the bare TLD (never scopes a clear to `.global` /
 * `.localhost`), and yields nothing for single-label or IP hosts.
 */
export function deriveParentDomains(hostname: string): string[] {
  if (!hostname || /^[0-9.]+$/.test(hostname)) {
    return [];
  }
  const labels = hostname.split('.');
  const out: string[] = [];
  // i starts at 1 (drop the leading label) and stops before the last label so
  // we never emit a bare-TLD scope.
  for (let i = 1; i < labels.length - 1; i += 1) {
    out.push('.' + labels.slice(i).join('.'));
  }
  return out;
}

export interface ClearAuthCookieOptions {
  /**
   * The cookie name to clear. Defaults to `'jwt-auth'`, matching
   * `settings.SIMPLE_JWT['AUTH_COOKIE']` server-side. Override if
   * your backend uses a different name.
   */
  name?: string;
  /**
   * Domain scopes to iterate. Defaults to `DEFAULT_CANDIDATE_DOMAINS`.
   * Pass a custom list to add an environment-specific scope (e.g.
   * `.staging.aegix.global`).
   */
  candidateDomains?: readonly string[];
  /**
   * Path attribute on the unset. Defaults to `'/'`. Cookies set on
   * a sub-path can't be cleared by a `/` unset; you'd need a
   * matching path. We default to `/` because that's what
   * `set_token_expiration` uses server-side.
   */
  path?: string;
  /**
   * Document accessor for testing. Defaults to the global
   * `document`. Passing a mock document lets tests inspect what was
   * written to `document.cookie` without manipulating real cookies.
   */
  documentRef?: Pick<Document, 'cookie'>;
  /**
   * `location.hostname` accessor for testing. Defaults to the
   * global `location.hostname`. The helper also implicitly clears
   * the current hostname even if it's not in `candidateDomains`.
   */
  hostname?: string;
}

/**
 * Clears the named cookie across every candidate `Domain` scope.
 *
 * Returns the number of `Set-Cookie`-style writes performed, mostly
 * for testability. Callers don't usually need to act on it.
 *
 * Example:
 *
 *   clearAuthCookie();                           // clear `jwt-auth`
 *   clearAuthCookie({ name: 'session' });        // custom cookie name
 *   clearAuthCookie({
 *     candidateDomains: ['', '.staging.aegix.global'],
 *   });
 */
export function clearAuthCookie(
  options: ClearAuthCookieOptions = {},
): number {
  const {
    name = 'jwt-auth',
    candidateDomains = DEFAULT_CANDIDATE_DOMAINS,
    path = '/',
    documentRef = typeof document !== 'undefined' ? document : null,
    hostname = typeof location !== 'undefined' ? location.hostname : '',
  } = options;

  if (!documentRef) {
    // Server-side rendering or test environment without document.
    // No cookies to clear; return 0.
    return 0;
  }

  // Build the unique set of domain attributes to write. Include
  // the current hostname implicitly so we don't depend on the
  // caller knowing to pass it.
  const scopes = new Set<string>();
  for (const d of candidateDomains) {
    scopes.add(d);
  }
  if (hostname) {
    scopes.add(hostname);
    // Also clear the env-scoped parent domain(s) the server sets the
    // cookie under (e.g. `.dev.aegix.global`), which the hardcoded default list
    // misses on dev/staging.
    for (const parent of deriveParentDomains(hostname)) {
      scopes.add(parent);
    }
  }

  let writes = 0;
  for (const scope of scopes) {
    const domainAttr = scope === '' ? '' : `; domain=${scope}`;
    documentRef.cookie =
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainAttr}`;
    writes += 1;
  }
  return writes;
}
